/** @jsxImportSource @emotion/react */
import { Container, Link, Stack, Text } from "basis";
import { CreditCard } from "components/CreditCard/CreditCard";
import { DownloadButton } from "components/DownloadButton/DownloadButton";
import { Layout } from "components/Layout/Layout";
import { ResponsiveRow } from "components/ResponsiveRow/ResponsiveRow";
import { DebitCardReceiptPdf } from "components/WaysToPay/DebitCardSuccessPage/DebitCardReceiptPdf";
import { WaysToPayFAQ } from "components/WaysToPay/WaysToPayFAQ";
import { format } from "date-fns";
import { formatAmount } from "library/moneyUtils";
import { useMemo } from "react";
import { useLocation } from "react-router-dom";

interface NavigationState {
  product: {
    cardName: string;
    cardEndsWith: string;
  };
  amount: number;
  receiptNumber: string;
  paymentDate: string;
  debitCardEndsWith: string;
}

export const DebitCardSuccessPage = () => {
  const location = useLocation();

  const state = location.state as NavigationState;

  const { product, amount, receiptNumber, paymentDate, debitCardEndsWith } =
    state || {};

  const formattedAmount = formatAmount(amount, { withCents: true });
  const detailsPdf = useMemo(
    () => (
      <DebitCardReceiptPdf
        product={product}
        debitCardEndsWith={debitCardEndsWith}
        amount={amount}
        paymentDate={new Date(paymentDate)}
        receiptNumber={receiptNumber}
      />
    ),
    [product, amount, receiptNumber, paymentDate, debitCardEndsWith],
  );

  return (
    <Layout>
      <Layout.Header icon="success">Payment successful</Layout.Header>
      <Layout.Main>
        <Stack gap="8">
          <Container bg="grey.t05" padding="4">
            <CreditCard
              cardName={product.cardName}
              cardEndsWith={product.cardEndsWith}
            />
          </Container>
          <Container hasBorder padding="4">
            <Stack gap="4">
              <ResponsiveRow ratio="3/8">
                <Text color="grey.t75">Amount</Text>
                <Text>
                  <strong>{formattedAmount}</strong>
                </Text>
              </ResponsiveRow>
              <ResponsiveRow ratio="3/8">
                <Text color="grey.t75">Payment method</Text>
                <Text>
                  <strong>Debit card</strong>
                </Text>
              </ResponsiveRow>
              <ResponsiveRow ratio="3/8">
                <Text color="grey.t75">Card ending</Text>
                <Text>
                  <strong>{`•••• ${debitCardEndsWith}`}</strong>
                </Text>
              </ResponsiveRow>
              <ResponsiveRow ratio="3/8">
                <Text color="grey.t75">Payment date</Text>
                <Text>
                  <strong>{format(new Date(paymentDate), "dd/MM/yyyy")}</strong>
                </Text>
              </ResponsiveRow>
              <ResponsiveRow ratio="3/8">
                <Text color="grey.t75">Receipt number</Text>
                <>
                  <DownloadButton
                    pdf={detailsPdf}
                    fileName={receiptNumber}
                    variant="text"
                  >
                    {receiptNumber}
                  </DownloadButton>
                  <Text margin="2 0 0" textStyle="legal">
                    Please keep a copy of your receipt for your records.
                  </Text>
                </>
              </ResponsiveRow>
            </Stack>
          </Container>
          <Container>
            <Link appearance="primary-button" href="/" newTab={false}>
              Go to overview
            </Link>
          </Container>
        </Stack>
      </Layout.Main>
      <Layout.Sidebar>
        <WaysToPayFAQ />
      </Layout.Sidebar>
    </Layout>
  );
};
